/**
 * Единый формат хранения справочных полей: { handbookId, id }.
 *
 * Сохранение (toRef): любое значение (полный объект справочника, ID-строка/число
 * или название) приводится к минимальному { handbookId, id }.
 * Загрузка (refToId / refToFullObject / refToName): принимает НОВЫЙ формат
 * { handbookId, id }, СТАРЫЙ полный объект { id, name, ... } и СТАРУЮ строку
 * (ID или название) — для обратной совместимости с ранее сохранёнными документами.
 */

/** Находит справочник в массиве handbooks по списку подстрок названия. */
export const findHandbookIdByName = (handbooks, names, fallbackId = null) => {
  if (Array.isArray(handbooks)) {
    const list = Array.isArray(names) ? names : [names];
    const hb = handbooks.find(h =>
      h && h.name && list.some(n => h.name.includes(n) || n.includes(h.name))
    );
    if (hb) return hb.id;
  }
  return fallbackId;
};

const getHandbook = (handbooks, handbookId) =>
  Array.isArray(handbooks) ? handbooks.find(h => h && String(h.id) === String(handbookId)) : null;

/** Ищет запись в конкретном справочнике по ID. */
const findRefById = (handbook, id) => {
  if (!handbook || !Array.isArray(handbook.references) || id == null || id === '') return null;
  return handbook.references.find(r => String(r.id) === String(id)) || null;
};

/** Ищет запись в конкретном справочнике по названию (без учёта регистра/пробелов). */
const findRefByName = (handbook, name) => {
  if (!handbook || !Array.isArray(handbook.references) || !name) return null;
  const target = String(name).trim().toLowerCase();
  return (
    handbook.references.find(r => String(r.name || '').trim() === String(name).trim()) ||
    handbook.references.find(r => String(r.name || '').trim().toLowerCase() === target) ||
    null
  );
};

/**
 * Приводит значение к { handbookId, id } для сохранения.
 * @returns {{handbookId:number, id:(number|string)} | null}
 */
export const toRef = (value, handbookId, handbooks = []) => {
  if (value === null || value === undefined || value === '') return null;
  if (handbookId == null) return null;

  // Уже объект справочника / ref
  if (typeof value === 'object') {
    if (value.id !== undefined && value.id !== null && value.id !== '') {
      const handbook = getHandbook(handbooks, handbookId);
      if (handbook && !findRefById(handbook, value.id) && value.name) {
        const refByName = findRefByName(handbook, value.name);
        if (refByName) {
          return { handbookId, id: refByName.id };
        }
      }
      return { handbookId, id: value.id };
    }
    // Старый объект без id, но с name — пробуем разрешить
    if (value.name) {
      const ref = findRefByName(getHandbook(handbooks, handbookId), value.name);
      return ref ? { handbookId, id: ref.id } : null;
    }
    return null;
  }

  const raw = String(value).trim();
  if (!raw) return null;

  // Числовая строка → это ID
  if (!isNaN(raw)) {
    return { handbookId, id: isNaN(parseInt(raw, 10)) ? raw : parseInt(raw, 10) };
  }

  // Название → ищем в справочнике
  const ref = findRefByName(getHandbook(handbooks, handbookId), raw);
  return ref ? { handbookId, id: ref.id } : null;
};

/** Извлекает ID (строкой) из значения любого формата. '' если нет. */
export const refToId = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'object') {
    return value.id !== undefined && value.id !== null ? String(value.id) : '';
  }
  return String(value);
};

/**
 * Возвращает полный объект справочника { id, name, ... } из значения любого формата.
 * Для нового { handbookId, id } разрешает запись из handbooks.
 */
export const refToFullObject = (value, handbooks = []) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'object') {
    // Старый полный объект (есть name) — возвращаем как есть (без служебного handbookId)
    if (value.name !== undefined) {
      const { handbookId, ...rest } = value;
      return { id: value.id, name: value.name || '', ...rest };
    }
    // Новый формат { handbookId, id } — разрешаем из справочника
    const ref = findRefById(getHandbook(handbooks, value.handbookId), value.id);
    if (ref) {
      return {
        id: ref.id,
        name: ref.name || '',
        ...Object.fromEntries(Object.entries(ref).filter(([k]) => k !== 'id' && k !== 'name')),
      };
    }
    return value.id !== undefined ? { id: value.id, name: '' } : '';
  }
  return String(value);
};

/**
 * Возвращает название записи справочника из значения любого формата.
 * Для нового { handbookId, id } разрешает name из handbooks.
 */
export const refToName = (value, handbooks = []) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'object') {
    if (value.name) return value.name;
    const ref = findRefById(getHandbook(handbooks, value.handbookId), value.id);
    return ref ? ref.name || '' : '';
  }
  return String(value);
};
